import { defineStore, acceptHMRUpdate } from 'pinia'
import { apiClient } from '@/helpers/api.js'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useCardStore } from '@/stores/CardStore'
import { LISTING_STATUS, POLICIES_IDS_NAMES_IN_TEMPLATE, AVAILABLE_FILTERS } from '@/constants'
import { useUserStore } from '@/stores/UserStore'

export const useListingsStore = defineStore('ListingsStore', {
  state: () => {
    return {
      activeConnectionData: null,
      listingsLoading: false,
      listingsLoadingMore: false,
      listingsDetailsLoading: false,
      additionalInfoLoader: false,
      prevNextIdsLoader: false,
      additionalInfoAlreadyFetched: false,
      correctListingsLoader: false,
      listingsView: 'List',
      openedListingDialog: false,
      selectedCardDataWithBeforeUpdate: {},
      listings: [],
      originalListings: [],
      endedListings: [],
      totalListings: {
        for_sale: 0,
        ended: 0
      },
      pages: {
        for_sale: 1,
        ended: 1
      },
      count: {
        collx: 0,
        ebay: 0,
        whatnot: 0,
        shopify: 0,
      },
      nextListingId: null,
      previousListingId: null,
      isDirty: false,
      visibleListingList: '',
      setValuesToDefault: false
    }
  },

  getters: {
    listingsByStatus () {
      return {
        for_sale: { key: 'for_sale', values: this.listings },
        ended: { key: 'ended', values: this.endedListings }
      }
    }
  },

  actions: {
    async fetchListings(type, section = 'for_sale', isPagination = false) {
      if (type === 'empty') return

      const cardStore = useCardStore()

      try {
        this.fetchListingsAdditionalInfo()

        // Set appropriate loading state based on pagination
        if (this.pages[section] > 1) {
          this.listingsLoadingMore = true
        } else {
          this.listingsLoading = true
        }

        let listingList = { data: { available_filters: AVAILABLE_FILTERS } }
        let endedListingList = { data: { available_filters: AVAILABLE_FILTERS } }

        delete cardStore.itemFilters.status

        if (!this.visibleListingList || this.visibleListingList === 'for_sale') {
          if (this.visibleListingList === 'for_sale') {
            this.endedListings = []
          }

          // Base parameters for API request
          const baseParams = {
            page: this.pages[section],
            perPage: 10,
            type
          }

          // Combine all filters into a single filter parameter with comma separation, excluding 'q'
          const filterParams = Object.entries(cardStore.itemFilters).length 
            ? `filter=${Object.entries(cardStore.itemFilters)
                .filter(([key]) => key !== 'q')
                .map(([key, value]) => `${key}=${value}`)
                .join(',')}`
            : ''

          const url = `/listings${filterParams ? '?' + filterParams : ''}${
            Object.keys(baseParams).length ? (filterParams ? '&' : '?') + new URLSearchParams({
              ...baseParams,
              ...(cardStore.itemFilters.q ? { q: cardStore.itemFilters.q } : {})
            }).toString() : ''
          }`

          // Fetch active listings
          listingList = await apiClient().get(url)

          // Process active listings data
          const updatedListings = listingList.data.items.reduce((acc, item) => {
            // Merge card and listing data, add status fields
            acc.push({ 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              listingStatus: LISTING_STATUS.for_sale.key 
            })
            return acc
          }, [])

          // Update active listings state - either append or replace
          this.listings = isPagination 
            ? [...this.listings, ...updatedListings] 
            : updatedListings

          this.totalListings.for_sale = listingList.data.total_pages
        }


        if (!this.visibleListingList || this.visibleListingList === 'ended') {
          if (this.visibleListingList === 'ended') {
            this.listings = []
          }
          // Construct URL for ended listings using same filters
          const endedFilterParams = Object.entries(cardStore.itemFilters).length
          ? `filter=${Object.entries(cardStore.itemFilters)
              .filter(([key]) => key !== 'q')
              .map(([key, value]) => `${key}=${value}`)
              .join(',')}`
          : ''

          const endedBaseParams = {
            ended: true,
            perPage: 10,
            page: this.pages[section],
            type
          }

          const endedUrl = `/listings${endedFilterParams ? '?' + endedFilterParams : ''}${
          Object.keys(endedBaseParams).length ? (endedFilterParams ? '&' : '?') + new URLSearchParams({
            ...endedBaseParams,
            ...(cardStore.itemFilters.q ? { q: cardStore.itemFilters.q } : {})
          }).toString() : ''
          }`

          // Fetch ended listings
          endedListingList = await apiClient().get(endedUrl)

          // Process ended listings data
          const updatedEndedListings = endedListingList.data.items.reduce((acc, item) => {
            // Merge card and listing data, add status fields
            acc.push({ 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              listingStatus: LISTING_STATUS.ended.key 
            })
            return acc
          }, [])
          
          // Update ended listings state - either append or replace
          this.endedListings = isPagination
            ? [...this.endedListings, ...updatedEndedListings]
            : updatedEndedListings

          this.totalListings.ended = endedListingList.data.total_pages
        }

        // Reset loading states
        this.listingsLoading = false
        this.listingsLoadingMore = false

      } catch (error) {
        console.error('fetchListings error', error)
        // Reset loading states on error
        this.listingsLoading = false
        this.listingsLoadingMore = false
      }
    },

    async fetchListingsAdditionalInfo() {
      const cardStore = useCardStore()

      try {
        if (this.additionalInfoAlreadyFetched) return

        this.additionalInfoLoader = true

        const { data } = await apiClient().get(`/listings/info`)

        // Update count state which we show on sidebar menu
        this.count = Object.entries(this.count).reduce((acc, [key, value]) => {
          acc[key] = data[`${key}_listings_active`] + data[`${key}_listings_inactive`]
          return acc
        }, {})

        cardStore.availableFilters = {
          attribute: [ ...data.available_filters?.attribute ],
          brand: [ ...data.available_filters?.brand ],
          category: [ ...data.available_filters?.category ],
          graded: [ ...data.available_filters?.graded ],
          grader: [ ...data.available_filters?.grader ],
          team: [ ...data.available_filters?.team ],
          status: { ...data.available_filters?.status },
        }

        if (this.activeConnectionData) {
          this.totalListings.for_sale = data[`${this.activeConnectionData.key}_listings_active`]
          this.totalListings.ended = data[`${this.activeConnectionData.key}_listings_inactive`]
        }

        this.additionalInfoLoader = false
        this.additionalInfoAlreadyFetched = true
      } catch (error) {
        this.additionalInfoLoader = false
        console.error('fetchListingsAdditionalInfo error', error)
      }
    },

    async fetchListingById(type, id) {
      const connectionStore = useConnectionStore()
      const userStore = useUserStore()
      const cardStore = useCardStore()

      try {
        this.listingsDetailsLoading = true

        const savedData = JSON.parse(localStorage.getItem('savedListingData')) || {}

        const { data } = await apiClient().get(`/listings/${id}?type=${type}`)

        const listingStatus = data.listing.quantity_available > 0 ? LISTING_STATUS.for_sale.key : data.listing.quantity_sold > 0 ? LISTING_STATUS.ended.key : LISTING_STATUS.for_sale.key 

        cardStore.selectedCardData = {
          ...data,
          ...data.listing, 
          ...data.card, 
          status: data.card.status, 
          id: data.card.id,
          set: data.card.set,
          number: data.card.number,
          title: data.card.title,
          description: data.card.description,
          player: data.card.player,
          team: data.card.team,
          year: data.card.year,
          listingStatus,
          listingId: data.listing.id,
          attributes: data.card.flags,
          readonlyListing: data.listing.read_only,
          ebayListingId: data.listing.ebay_listing_id,
          whatnotListingId: data.listing.whatnot_id,
          collx_price: data.listing.collx_price,
          ebay_price: data.listing.ebay_price,
          whatnot_price: data.listing.whatnot_price,
          shopify_price: data.listing.shopify_price,
          has_price_recommendation: data.card.has_price_recommendation,
          price_recommendation: data.card.price_recommendation,
        }

        this.selectedCardDataWithBeforeUpdate = { 
          ...cardStore.selectedCardData, 
          collx_quantity_available: data.listing.collx_quantity_available,
          ebay_quantity_available: data.listing.ebay_quantity_available,
          whatnot_quantity_available: data.listing.whatnot_quantity_available,
        }

        if (savedData[userStore.user.id] && savedData[userStore.user.id][cardStore.selectedCardData.id]) {
          cardStore.selectedCardData = { ...cardStore.selectedCardData, ...savedData[userStore.user.id][cardStore.selectedCardData.id] }
          this.isDirty = true
        }

        connectionStore.selectedTemplate = {
          ebay_location_key: cardStore.selectedCardData.location_key,
          ebay_location_id: cardStore.selectedCardData.location_id,
          [POLICIES_IDS_NAMES_IN_TEMPLATE.fulfillmentPolicies]: cardStore.selectedCardData.fulfillment_id,
          [POLICIES_IDS_NAMES_IN_TEMPLATE.paymentPolicies]: cardStore.selectedCardData.payment_id,
          [POLICIES_IDS_NAMES_IN_TEMPLATE.returnPolicies]: cardStore.selectedCardData.return_id,
        }

        this.fetchNextPrevIds()
        
        this.listingsDetailsLoading = false

        //this.fetchListings(type, listingStatus)
      } catch (error) { 
        console.error('fetchListingById error', error)
        this.listingsDetailsLoading = false
      }
    },

    async updateListingLocally(body) {
      const cardStore = useCardStore()

      try {
        cardStore.selectedCardData = { ...cardStore.selectedCardData, ...body }

      } catch (error) {
        console.error('updateListingLocally error', error)
      }
    },

    async updateListing(body) {
      const cardStore = useCardStore()
      try {
        await apiClient().patch(`/listings/${cardStore.selectedCardData.listingId}`, body) 
      } catch (error) {
        console.error('updateListing error', error)
      }
    },

    async publishListing() {
      const cardStore = useCardStore()

      try {
        let updatedData = {}

        if (this.activeConnectionData.key === 'ebay') {
          updatedData = {
            ebay_price: cardStore.selectedCardData.ebay_price,
            ebay_title: cardStore.selectedCardData.ebay_title,
            ebay_description: cardStore.selectedCardData.ebay_description,
            schedule_date: cardStore.selectedCardData.schedule_date,
            schedule_time: cardStore.selectedCardData.schedule_time
         }
        } else if (this.activeConnectionData.key === 'whatnot') {
          updatedData = {
            whatnot_price: cardStore.selectedCardData.whatnot_price,
            whatnot_description: cardStore.selectedCardData.whatnot_description,
            whatnot_title: cardStore.selectedCardData.whatnot_title,
            quantity: cardStore.selectedCardData.whatnot_quantity_available,
         }
        } else if (this.activeConnectionData.key === 'shopify') {
          updatedData = {
            shopify_price: cardStore.selectedCardData.shopify_price,
            shopify_description: cardStore.selectedCardData.shopify_description,
            shopify_title: cardStore.selectedCardData.shopify_title,
          }
        } else {
          updatedData = {
            collx_price: cardStore.selectedCardData.collx_price,
            collx_notes: cardStore.selectedCardData.collx_description,
         }
        }

        const body = {
          data:{
              type: this.activeConnectionData.key,
              listing_id: cardStore.selectedCardData.listingId,
              updated_data: updatedData
          }
      }

        await apiClient().post(`/jobs/update-listing`, body)       
      } catch (error) {
        console.error('publishListing error', error)
      }
    },

    async endListing(type, cardId = null, jobMethod = 'delete') {
      const cardStore = useCardStore()

      try {
        await apiClient().post(`/jobs/send-to-${type}`, {
          cards: [
            cardId || cardStore.selectedCardData.card.id
          ],
          data: {
            job_method: jobMethod
          }
        })
      } catch (error) {
        console.error('endListing error', error)
      }
    },

    async correctListings(type) {
      try {
        this.correctListingsLoader = true

        if (type === 'ebay') {
          await apiClient().post(`/${type}/notifications`)
          await apiClient().post(`/${type}/correct-listings`)
        }

        //this.fetchListings(type)

        this.correctListingsLoader = false
      } catch (error) {
        console.error('correctListings error', error)
        this.correctListingsLoader = false
      }
    },

    async fetchNextPrevIds() {
      const cardStore = useCardStore()

      try {
        this.prevNextIdsLoader = true

        const { data } = await apiClient().get(`/listings/${cardStore.selectedCardData.listingId}/prev-next?type=${this.activeConnectionData.key}${cardStore.selectedCardData.listingStatus === 'ended' ? '&ended=true' : ''}`)

        const { next_listing_id, prev_listing_id, total_listings, current_index } = data

        this.nextListingId = next_listing_id
        this.previousListingId = prev_listing_id

        cardStore.totalCards = total_listings
        cardStore.cardIndex = current_index

        this.prevNextIdsLoader = false
      } catch (error) {
        console.log('fetchNextPrevIds error inventory', error)
        this.prevNextIdsLoader = false
      }
    },

  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useListingsStore, import.meta.hot))
}